import { inject, Injectable } from '@angular/core';

import { JwtInfo } from '@hsc/common/schemas/eua.schema.js';
import { normalizeJwtContext } from '@hsc/common/utilities/jwt-utils';
import { SplunkLoggerService } from '@services/splunk-logger.service';
import { ApigeeError } from '@utilities/http-utilities';
import { toError } from '@utilities/to-error';
import { environment } from '@environments/environment';

const KNOWN_ERRORS: Record<string, 'ignore' | 'expected'> = {
  'Client error expected': 'expected',
  // TODO: remove if we can migrate to fullstory npm package
  "Failed to set the 'text' property on 'HTMLScriptElement': Identifier 'allowedAttributes' has already been declared":
    'ignore',
};

@Injectable({ providedIn: 'root' })
export class NewRelicService {
  // Properties
  private readonly splunkLoggerService = inject(SplunkLoggerService);

  // Getters/Setters
  private getNewRelic(): typeof window.newrelic | undefined {
    return window.newrelic;
  }

  // Methods
  initialize() {
    const newRelic = this.getNewRelic();

    // Skip for local as no script loaded in index.html
    if (environment.NAME === 'local') return;

    if (newRelic) {
      this.splunkLoggerService.info('New Relic initialized');
    }

    if (!newRelic?.setErrorHandler) {
      const error = new Error('New Relic error handler not registered');
      this.splunkLoggerService.error(error);
      this.noticeError(error);
      return;
    }

    // The New Relic browser agent operates outside of the Angular zone
    // We cannot use the CustomErrorHandler to skip reporting ApigeeErrors
    newRelic.setErrorHandler((error: unknown) => {
      if (error instanceof ApigeeError) return true;

      // Report all other errors to New Relic
      return false;
    });
  }

  identifyUser(jwtInfo: JwtInfo) {
    const newRelic = this.getNewRelic();
    const newRelicVars = normalizeJwtContext(jwtInfo);
    Object.entries(newRelicVars).forEach(([key, value]) => {
      newRelic?.setCustomAttribute?.(key, value);
    });
    this.splunkLoggerService.info('New Relic user identified');
  }

  noticeError(error: unknown) {
    const normalizedError = toError(error);
    const handling = KNOWN_ERRORS[normalizedError.message];

    // Completely ignore sending errors to New Relic
    if (handling === 'ignore') return;

    // Send errors to gather metric but not to be alerted on
    const newRelicAttributes: Record<string, string | number | boolean> = {};
    if (handling === 'expected') newRelicAttributes['expected'] = true;

    if (environment.FULLSTORY_ENABLED) {
      // Must invoke manually to prevent circular dependency between FullstoryService and NewRelicService
      const fullstoryUrl = window.FS?.getCurrentSessionURL?.(true);
      if (fullstoryUrl) this.getNewRelic()?.setCustomAttribute?.('fullstoryUrl', fullstoryUrl);
    }

    this.getNewRelic()?.noticeError?.(normalizedError, newRelicAttributes);
  }
}
